import { compareAsc } from 'date-fns';

// Validators provide field-level validation rules. If the field is valid it
// should return undefined. If the field is invalid it should return an error
// (usually, but not necessarily, a String).

/**
 * Returns a validator for required fields.
 *
 * @param {String} error Error message to display when the field is invalid.
 */
export const required = (error) => (value) => (value ? undefined : error);

export const regPhoneNumber = (value) => (
  /^(0|[1-9][0-9]{9})$/i.test(value)
    ? undefined
    : 'Invalid phone number, must be 10 digits'
);

/**
 * Validates dates used for experience form fields.
 * The validator actually tries to match YYYY-MM-DD, which is returned by the
 * normalizer.
 *
 * @param {String} value Date value returned from form field normalization.
 */
export const regExperienceDate = (value) => (
  /^\d{4}-\d{2}-\d{2}$/i.test(value)
    ? undefined
    : 'Invalid date, must be MM/YYYY'
);


/**
 * Validates experience start dates for experience form fields.
 *
 * @param {String} value Date value returned from form field normalization.
 */
export const valExperienceStartDate = (value) => {
  if (!value) {
    return required('Add a Start Date')(value);
  }

  return regExperienceDate(value);
};

/**
 * Checks if the start date is after the end date
 * Note: this returns true if not all the actual numbers are there, useful for formik validation
 *
 * @param {Number} startYear
 * @param {Number} startMonth
 * @param {Number} endYear
 * @param {Number} endMonth
 * @returns {Boolean}
 */
export const valEndYearMonthAfterStartYearMonth = (startYear, startMonth, endYear, endMonth) => {
  const startYearInt = parseInt(startYear);
  const startMonthInt = parseInt(startMonth);
  const endYearInt = parseInt(endYear);
  const endMonthInt = parseInt(endMonth);
  if (
    // check if each date exists
    startYearInt
    && startMonthInt
    && endYearInt
    && endMonthInt
    // check if end date is before start date
    && (
      // case where start year is greater than end year
      startYearInt > endYearInt
      // case where equal years but start month is greater
      || (
        startYearInt >= endYearInt
        && startMonthInt > endMonthInt
      )
    )
  ) {
    return false;
  }
  return true;
};

/**
 * Checks if the iso start year is after the end year
 * Note: this returns true if both years aren't there are parse-able into integers ('In Progress'), useful for formik validation
 *
 * @param {Number} startYear
 * @param {Number} startMonth
 * @param {Number} endYear
 * @param {Number} endMonth
 * @returns {Boolean}
 */
export const valEndYearAfterStartYear = (startYear, endYear) => {
  const startYearInt = parseInt(startYear);
  const endYearInt = parseInt(endYear);
  if (startYearInt && endYearInt && startYearInt > endYearInt) {
    return false;
  }
  return true;
};

/**
 * Validates experience start date <= end date.
 *
 * @param {String} value Error message to display when not true.
 */
export const valExperienceEndAfterStartDate = (startDate, endDate) => {
  // if start date returns an error string, then just return undefined
  if (!valExperienceStartDate(startDate)) {
    if (!(compareAsc(new Date(startDate), new Date(endDate)) <= 0)) {
      return 'Start date must be before end date';
    }
  }
  return undefined;
};

/**
 * Validates experience end dates for experience form fields.
 *
 * The validator skips validation if "Current" is selected, and also checks the
 * value as a required field.
 *
 * @param {String} value Date value returned from form field normalization.
 * @param {Object} allValues Object containing all values in the form.
 * @param {Boolean} allValues.current Value of the "Current" checkbox field.
 */
export const valExperienceEndDate = (value, allValues) => {
  // skip validation if "Current" is selected
  if (allValues.current) { return undefined; }

  // validate as required field if empty
  if (!value) {
    return required('Add an End Date')(value);
  }

  return regExperienceDate(value) || valExperienceEndAfterStartDate(allValues.start_date, value);
};

export const valEducationEndAfterStartDate = (endDate, allValues) => {
  const { start_date: startDate } = allValues;
  if (!startDate || !endDate || (compareAsc(new Date(startDate), new Date(endDate)) <= 0)) {
    return undefined;
  }
  return 'Start date must be before end date';
};

/**
 * Returns true for valid URLs - application protocol (e.g. http(s)) optional
 * Adapted from: http://docs.jquery.com/Plugins/Validation/Methods/url
 * contributed by Scott Gonzalez: http://projects.scottsplayground.com/iri/
 * See also: app/view/static/js/zr-validate-additional-methods.js
 *
 * @param {String} value URL to validate
 */

// eslint-disable-next-line no-useless-escape
export const isValidUrl = (value) => /^((https?|ftp):\/\/)?(((([a-z0-9]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&amp;'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&amp;'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&amp;'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&amp;'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&amp;'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i.test(value);

/**
 * Validates a website URL on a jobseeker's profile.
 *
 * @param {String} value URL value
 * @param {String} regex Regex that URL must match
 * @param {String} errorMessage Error message to display if URL is invalid
 */
export const validateWebsite = (value, regex, errorMessage) => {
  // website URLs are not required
  if (!value) {
    return undefined;
  }

  // if no regex, just needs to be a valid URL (http(s) optional)
  // if regex is supplied, must also match
  if (isValidUrl(value) && (!regex || regex.test(value))) {
    return undefined;
  }

  // otherwise it's invalid input
  return errorMessage;
};

/**
 * Validates a jobseeker's personal website URL.
 *
 * @param {String} value Personal website URL value
 */
export const valPersonalWebsite = (value) => validateWebsite(value, null, 'Please enter a valid website');

/**
 * Checks that a jobseeker's Twitter profile URL contains twitter.com/ & is
 * a valid URL (http(s) optional).
 *
 * @param {String} value Twitter profile URL value
 */
export const valTwitter = (value) => validateWebsite(value, /twitter\.com\//i, 'Please enter a valid Twitter Profile URL');

/**
 *  Checks that a jobseeker's Facebook profile URL contains facebook.com/ &
 * is a valid URL (http(s) optional).
 *
 * @param {String} value Facebook profile URL value
 */
export const valFacebook = (value) => validateWebsite(value, /facebook\.com\//i, 'Please enter a valid Facebook Profile URL');

/**
 * Checks that a jobseeker's LinkedIn profile URL contains linkedin.com/ &
 * is a valid URL (http(s) optional).
 *
 * @param {String} value LinkedIn profile URL value
 */
export const valLinkedin = (value) => validateWebsite(value, /linkedin\.com\//i, 'Please enter a valid LinkedIn Profile URL');
